import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Notification, UserNotificationSettings } from '@/types';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  settings: UserNotificationSettings | null;

  addNotification: (notification: Notification) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  setConnected: (connected: boolean) => void;
  setSettings: (settings: UserNotificationSettings) => void;
  updateSettings: (settings: Partial<UserNotificationSettings>) => void;
}

const MAX_NOTIFICATIONS = 100;

const countUnread = (notifications: Notification[]): number =>
  notifications.filter((n) => !n.read).length;

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set) => ({
      notifications: [],
      unreadCount: 0,
      isConnected: false,
      settings: null,

      addNotification: (notification) =>
        set((state) => {
          const exists = state.notifications.some((n) => n.id === notification.id);
          if (exists) return state;

          const newNotifications = [notification, ...state.notifications].slice(0, MAX_NOTIFICATIONS);
          return {
            notifications: newNotifications,
            unreadCount: state.unreadCount + (notification.read ? 0 : 1),
          };
        }),

      markAsRead: (id) =>
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          // Only decrement if the notification was actually unread
          if (!notification || notification.read) return state;
          return {
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        }),

      markAllAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        })),

      removeNotification: (id) =>
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: notification?.read ? state.unreadCount : Math.max(0, state.unreadCount - 1),
          };
        }),

      clearNotifications: () =>
        set({ notifications: [], unreadCount: 0 }),

      setConnected: (connected) => set({ isConnected: connected }),

      setSettings: (settings) => set({ settings }),

      updateSettings: (settings) =>
        set((state) => ({
          settings: state.settings ? { ...state.settings, ...settings } : settings as UserNotificationSettings,
        })),
    }),
    {
      name: 'fitness-notifications',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        notifications: state.notifications.slice(0, 50),
        settings: state.settings,
      }),
      // Recompute unreadCount from persisted notifications on rehydrate
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.unreadCount = countUnread(state.notifications);
        }
      },
    }
  )
);
